import type { LucideIcon } from 'lucide-react'
import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronRight,
  CloudOff,
  Minus,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { parseAmount } from '@aeris/finance'
import { formatMoney, toNumber } from '@aeris/money'
import { Spinner, VisuallyHidden, cn } from '@aeris/ui'

/**
 * As peças do painel do Início — F219.
 *
 * O dono: *"o Início tem que de fato parecer um dashboard… as informações
 * principais estão escondidas"*. O painel tinha um só tamanho de número para
 * tudo: "Vencido a receber" e "Cancelados" pesavam o mesmo. Aqui está o
 * número GRANDE, para o que se olha primeiro, e a barra que mostra de que lado
 * está o vale.
 *
 * Só tokens, sem gradiente e sem biblioteca de gráfico (`docs/UI_UX.md`): o
 * painel abre no computador do caixa.
 */

const brl = (amount: string): string => formatMoney(parseAmount(amount))

export type KpiTone = 'neutral' | 'warning' | 'danger'

const ICON_TONE: Record<KpiTone, string> = {
  neutral: 'bg-(--brand-primary-subtle) text-(--brand-primary)',
  warning: 'bg-(--warning-subtle) text-(--warning)',
  danger: 'bg-(--danger-subtle) text-(--danger)',
}

/**
 * O número grande do topo.
 *
 * O rótulo é um `<p>` cujo pai guarda também o valor e a dica — a mesma forma
 * do `StatTile`, para quem procura o cartão pelo nome achar o número junto.
 * Com `onOpen`, o cartão inteiro é um botão que leva à tela do número.
 */
export const KpiTile = ({
  label,
  value,
  hint,
  icon: Icon,
  tone = 'neutral',
  loading = false,
  error = false,
  onOpen,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
  icon: LucideIcon
  tone?: KpiTone
  loading?: boolean
  error?: boolean
  onOpen?: () => void
}): ReactNode => {
  const body = (
    <>
      <span
        aria-hidden="true"
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-(--radius-md)',
          ICON_TONE[tone]
        )}
      >
        <Icon className="h-4.5 w-4.5" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-(length:--text-sm) text-(--text-secondary)">
          {label}
        </p>
        <div
          className={cn(
            'truncate font-(--font-mono) text-(length:--text-2xl) leading-(--leading-tight) font-(--weight-semibold) tabular-nums',
            tone === 'danger' ? 'text-(--danger)' : 'text-(--text-primary)'
          )}
        >
          {loading ? (
            <Spinner label={`Carregando ${label}`} />
          ) : error ? (
            <span className="flex items-center gap-1.5 font-(--font-sans) text-(length:--text-sm) font-(--weight-normal) text-(--text-secondary)">
              <CloudOff aria-hidden="true" className="h-4 w-4" />
              Não carregou
            </span>
          ) : (
            value
          )}
        </div>
        {hint === undefined || loading || error ? null : (
          <p className="truncate text-(length:--text-xs) text-(--text-secondary)">
            {hint}
          </p>
        )}
      </div>
      {onOpen === undefined ? null : (
        <ChevronRight
          aria-hidden="true"
          className="mt-1 h-4 w-4 shrink-0 text-(--text-muted)"
        />
      )}
    </>
  )

  const frame = cn(
    'flex min-w-0 items-start gap-3 rounded-(--radius-lg) border bg-(--surface) px-4 py-3.5 text-left',
    tone === 'danger'
      ? 'border-(--danger)'
      : tone === 'warning'
        ? 'border-(--warning)'
        : 'border-(--border-subtle)'
  )

  return onOpen === undefined ? (
    <div className={frame}>{body}</div>
  ) : (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        frame,
        'transition-colors duration-(--duration-fast) hover:bg-(--surface-hover) focus-visible:outline-2 focus-visible:outline-(--border-focus)'
      )}
    >
      {body}
    </button>
  )
}

/**
 * Dois lados de um total: o vale da loja e o da gestão antiga (F214).
 *
 * Uma barra só, com o texto ao lado — cor nunca sozinha (`docs/UI_UX.md`). A
 * tabela escondida é o que o leitor de tela lê no lugar da barra.
 */
export const SplitBar = ({
  caption,
  parts,
}: {
  caption: string
  parts: readonly [
    { label: string; amount: string },
    { label: string; amount: string },
  ]
}): ReactNode => {
  const values = parts.map((part) =>
    Math.max(toNumber(parseAmount(part.amount)), 0)
  )
  const total = values.reduce((sum, value) => sum + value, 0)
  const shares = values.map((value) =>
    total === 0 ? 0 : Math.round((value / total) * 100)
  )
  const COLORS = ['bg-(--brand-primary)', 'bg-(--warning)'] as const

  return (
    <figure className="flex flex-col gap-2">
      <div
        aria-hidden="true"
        className="flex h-3 w-full overflow-hidden rounded-(--radius-full) bg-(--surface-sunken)"
      >
        {total === 0
          ? null
          : values.map((value, index) => (
              <span
                key={parts[index]?.label}
                className={COLORS[index]}
                style={{ width: `${String((value / total) * 100)}%` }}
              />
            ))}
      </div>
      <figcaption className="flex flex-wrap gap-x-4 gap-y-1 text-(length:--text-xs) text-(--text-secondary)">
        {parts.map((part, index) => (
          <span
            key={part.label}
            className="inline-flex items-center gap-1.5"
            aria-hidden="true"
          >
            <span
              className={cn('h-2 w-2 rounded-(--radius-full)', COLORS[index])}
            />
            {`${part.label} ${brl(part.amount)} (${String(shares[index] ?? 0)}%)`}
          </span>
        ))}
      </figcaption>
      <VisuallyHidden>
        <table>
          <caption>{caption}</caption>
          <tbody>
            {parts.map((part, index) => (
              <tr key={part.label}>
                <th scope="row">{part.label}</th>
                <td>{brl(part.amount)}</td>
                <td>{`${String(shares[index] ?? 0)}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </VisuallyHidden>
    </figure>
  )
}

/** "▲ 12% vs anterior", no sentido de quem lê: vender mais é bom. */
export const Trend = ({
  current,
  previous,
}: {
  current: string
  previous: string
}): ReactNode => {
  const now = toNumber(parseAmount(current))
  const before = toNumber(parseAmount(previous))
  if (before === 0) {
    return now === 0 ? (
      <span>igual ao período anterior</span>
    ) : (
      <span>sem venda no período anterior</span>
    )
  }
  const change = ((now - before) / before) * 100
  const rounded = Math.round(change)
  if (rounded === 0) {
    return (
      <span className="inline-flex items-center gap-1">
        <Minus aria-hidden="true" className="h-3 w-3" />
        igual ao período anterior
      </span>
    )
  }
  const up = rounded > 0
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1',
        up ? 'text-(--success)' : 'text-(--danger)'
      )}
    >
      {up ? (
        <ArrowUpRight aria-hidden="true" className="h-3 w-3" />
      ) : (
        <ArrowDownRight aria-hidden="true" className="h-3 w-3" />
      )}
      {`${up ? '+' : ''}${String(rounded)}% vs período anterior (${brl(previous)})`}
    </span>
  )
}
